// Bloque F de 018A — Técnica anestésica (Fase 1B, Tanda 13, ver PLAN.md; Ola 2 M5).
// Casillas COMBINABLES para las 3 categorías (el papel las imprime juntas, sin
// elegir una): cada categoría marcada revela su sub-formulario y solo lo marcado
// se guarda (`tecnicaAnestesicaDatos.ts`). Dentro de Regional, troncular y
// neuroaxial también son casillas combinables.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CampoDictado } from './CampoDictado';
import { SiNoBotones } from './camposSimples';
import { leerSiNo } from './camposSimplesDatos';
import {
  ABORDAJE_NEUROAXIAL_OPCIONES,
  APARATO_OPCIONES,
  CATEGORIAS_TECNICA,
  filaFarmacoSedoanalgesiaVacia,
  TOPE_FARMACOS_SEDOANALGESIA,
  CORMACK_OPCIONES,
  ACCESO_NEUROAXIAL_OPCIONES,
  INDUCCION_OPCIONES,
  LOCAL_OPCIONES,
  MANEJO_VIA_AEREA_OPCIONES,
  MANTENIMIENTO_OPCIONES,
  RAMSAY_NIVELES,
  SISTEMA_OPCIONES,
  TIPO_TUBO_OPCIONES,
  VIA_INTUBACION_OPCIONES,
  VISION_INTUBACION_OPCIONES,
  type CategoriaTecnicaAnestesica,
  type EstadoTecnicaAnestesica,
  type EstadoTecnicaGeneral,
  type EstadoTecnicaRegional,
  type EstadoTecnicaSedoanalgesia,
} from './tecnicaAnestesicaDatos';

function CampoSelect<T extends string>({
  id,
  label,
  value,
  opciones,
  etiquetaOpcion,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: T | '';
  readonly opciones: readonly T[];
  readonly etiquetaOpcion: (op: T) => string;
  readonly onChange: (v: T) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || undefined} onValueChange={(v) => onChange(v as T)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {opciones.map((op) => (
            <SelectItem key={op} value={op}>
              {etiquetaOpcion(op)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CampoTexto({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return <CampoDictado id={id} etiqueta={label} valor={value} onChange={onChange} />;
}

function CampoCheckbox({ id, label, checked, onChange }: { readonly id: string; readonly label: string; readonly checked: boolean; readonly onChange: (v: boolean) => void }): JSX.Element {
  return (
    <div className="flex items-center gap-2 self-end pb-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
    </div>
  );
}

function BloqueGeneral({ valor, onChange }: { readonly valor: EstadoTecnicaGeneral; readonly onChange: (cambios: Partial<EstadoTecnicaGeneral>) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <CampoSelect id="f018a-tec-sistema" label={t('formularios.f018a.bloqueF.general.sistema')} value={valor.sistema} opciones={SISTEMA_OPCIONES} etiquetaOpcion={(op) => t(`formularios.f018a.bloqueF.general.sistemaOpciones.${op}`)} onChange={(v) => onChange({ sistema: v })} />
      <CampoSelect id="f018a-tec-aparato" label={t('formularios.f018a.bloqueF.general.aparato')} value={valor.aparato} opciones={APARATO_OPCIONES} etiquetaOpcion={(op) => t(`formularios.f018a.bloqueF.general.aparatoOpciones.${op}`)} onChange={(v) => onChange({ aparato: v })} />
      <CampoSelect
        id="f018a-tec-manejo-via-aerea"
        label={t('formularios.f018a.bloqueF.general.manejoViaAerea')}
        value={valor.manejoViaAerea}
        opciones={MANEJO_VIA_AEREA_OPCIONES}
        etiquetaOpcion={(op) => t(`formularios.f018a.bloqueF.general.manejoViaAereaOpciones.${op}`)}
        onChange={(v) => onChange({ manejoViaAerea: v })}
      />
      <CampoSelect
        id="f018a-tec-via-intubacion"
        label={t('formularios.f018a.bloqueF.general.viaIntubacion')}
        value={valor.viaIntubacion}
        opciones={VIA_INTUBACION_OPCIONES}
        etiquetaOpcion={(op) => t(`formularios.f018a.bloqueF.general.viaIntubacionOpciones.${op}`)}
        onChange={(v) => onChange({ viaIntubacion: v })}
      />
      <CampoSelect
        id="f018a-tec-vision-intubacion"
        label={t('formularios.f018a.bloqueF.general.visionIntubacion')}
        value={valor.visionIntubacion}
        opciones={VISION_INTUBACION_OPCIONES}
        etiquetaOpcion={(op) => t(`formularios.f018a.bloqueF.general.visionIntubacionOpciones.${op}`)}
        onChange={(v) => onChange({ visionIntubacion: v })}
      />
      <CampoSelect id="f018a-tec-tipo-tubo" label={t('formularios.f018a.bloqueF.general.tipoTubo')} value={valor.tipoTubo} opciones={TIPO_TUBO_OPCIONES} etiquetaOpcion={(op) => t(`formularios.f018a.bloqueF.general.tipoTuboOpciones.${op}`)} onChange={(v) => onChange({ tipoTubo: v })} />
      <CampoTexto id="f018a-tec-diametro-tubo" label={t('formularios.f018a.bloqueF.general.diametroTubo')} value={valor.diametroTubo} onChange={(v) => onChange({ diametroTubo: v })} />
      <SiNoBotones id="f018a-tec-balon" etiqueta={t('formularios.f018a.bloqueF.general.balon')} valor={leerSiNo(valor.balon)} textos={{ si: t('formulario.si'), no: t('formulario.no') }} onChange={(v) => onChange({ balon: v })} />
      <SiNoBotones id="f018a-tec-taponamiento" etiqueta={t('formularios.f018a.bloqueF.general.taponamiento')} valor={leerSiNo(valor.taponamiento)} textos={{ si: t('formulario.si'), no: t('formulario.no') }} onChange={(v) => onChange({ taponamiento: v })} />
      <CampoTexto id="f018a-tec-equipo-intubacion" label={t('formularios.f018a.bloqueF.general.equipoIntubacion')} value={valor.equipoIntubacion} onChange={(v) => onChange({ equipoIntubacion: v })} />
      <CampoSelect id="f018a-tec-cormack" label={t('formularios.f018a.bloqueF.general.cormack')} value={valor.cormack} opciones={CORMACK_OPCIONES} etiquetaOpcion={(op) => op} onChange={(v) => onChange({ cormack: v })} />
      <CampoTexto id="f018a-tec-intentos-cormack" label={t('formularios.f018a.bloqueF.general.intentosCormack')} value={valor.intentosCormack} onChange={(v) => onChange({ intentosCormack: v })} />
      <CampoSelect id="f018a-tec-induccion" label={t('formularios.f018a.bloqueF.general.induccion')} value={valor.induccion} opciones={INDUCCION_OPCIONES} etiquetaOpcion={(op) => t(`formularios.f018a.bloqueF.general.induccionOpciones.${op}`)} onChange={(v) => onChange({ induccion: v })} />
      <CampoSelect
        id="f018a-tec-mantenimiento"
        label={t('formularios.f018a.bloqueF.general.mantenimiento')}
        value={valor.mantenimiento}
        opciones={MANTENIMIENTO_OPCIONES}
        etiquetaOpcion={(op) => t(`formularios.f018a.bloqueF.general.mantenimientoOpciones.${op}`)}
        onChange={(v) => onChange({ mantenimiento: v })}
      />
    </div>
  );
}

function BloqueRegional({ valor, onChange }: { readonly valor: EstadoTecnicaRegional; readonly onChange: (cambios: Partial<EstadoTecnicaRegional>) => void }): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <CampoTexto id="f018a-tec-asepsia" label={t('formularios.f018a.bloqueF.regional.asepsiaCon')} value={valor.asepsiaCon} onChange={(v) => onChange({ asepsiaCon: v })} />
        <CampoTexto id="f018a-tec-habon" label={t('formularios.f018a.bloqueF.regional.habonCon')} value={valor.habonCon} onChange={(v) => onChange({ habonCon: v })} />
        <CampoSelect id="f018a-tec-local" label={t('formularios.f018a.bloqueF.regional.local')} value={valor.local} opciones={LOCAL_OPCIONES} etiquetaOpcion={(op) => t(`formularios.f018a.bloqueF.regional.localOpciones.${op}`)} onChange={(v) => onChange({ local: v })} />
      </div>

      <div className="flex flex-row flex-wrap items-center gap-6">
        <Label>{t('formularios.f018a.bloqueF.regional.subtipo')}</Label>
        <CampoCheckbox id="f018a-tec-subtipo-troncular" label={t('formularios.f018a.bloqueF.regional.subtipoOpciones.troncular')} checked={valor.troncular} onChange={(v) => onChange({ troncular: v })} />
        <CampoCheckbox id="f018a-tec-subtipo-neuroaxial" label={t('formularios.f018a.bloqueF.regional.subtipoOpciones.neuroaxial')} checked={valor.neuroaxial} onChange={(v) => onChange({ neuroaxial: v })} />
      </div>

      {valor.troncular && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <CampoTexto id="f018a-tec-troncular-bloqueo" label={t('formularios.f018a.bloqueF.regional.troncular.bloqueoNervioPlexo')} value={valor.bloqueoNervioPlexo} onChange={(v) => onChange({ bloqueoNervioPlexo: v })} />
          <CampoTexto id="f018a-tec-troncular-intentos" label={t('formularios.f018a.bloqueF.regional.troncular.intentos')} value={valor.intentosTroncular} onChange={(v) => onChange({ intentosTroncular: v })} />
          <CampoTexto id="f018a-tec-troncular-anestesico" label={t('formularios.f018a.bloqueF.regional.troncular.anestesicoLocal')} value={valor.anestesicoLocalTroncular} onChange={(v) => onChange({ anestesicoLocalTroncular: v })} />
          <CampoTexto id="f018a-tec-troncular-coadyuvante" label={t('formularios.f018a.bloqueF.regional.troncular.coadyuvante')} value={valor.coadyuvanteTroncular} onChange={(v) => onChange({ coadyuvanteTroncular: v })} />
          <CampoTexto id="f018a-tec-troncular-tipo-aguja" label={t('formularios.f018a.bloqueF.regional.troncular.tipoAguja')} value={valor.tipoAgujaTroncular} onChange={(v) => onChange({ tipoAgujaTroncular: v })} />
          <CampoTexto id="f018a-tec-troncular-equipo" label={t('formularios.f018a.bloqueF.regional.troncular.equipo')} value={valor.equipoTroncular} onChange={(v) => onChange({ equipoTroncular: v })} />
        </div>
      )}

      {valor.neuroaxial && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <fieldset aria-labelledby="f018a-tec-neuroaxial-abordaje-titulo" className="m-0 flex min-w-0 flex-row flex-wrap items-center gap-4 border-0 p-0">
            <Label id="f018a-tec-neuroaxial-abordaje-titulo">{t('formularios.f018a.bloqueF.regional.neuroaxial.abordaje')}</Label>
            {ABORDAJE_NEUROAXIAL_OPCIONES.map((op) => (
              <CampoCheckbox
                key={op}
                id={`f018a-tec-neuroaxial-abordaje-${op}`}
                label={t(`formularios.f018a.bloqueF.regional.neuroaxial.abordajeOpciones.${op}`)}
                checked={valor.abordajesNeuroaxial.includes(op)}
                onChange={(v) => onChange({ abordajesNeuroaxial: v ? [...valor.abordajesNeuroaxial, op] : valor.abordajesNeuroaxial.filter((x) => x !== op) })}
              />
            ))}
          </fieldset>
          <CampoCheckbox id="f018a-tec-neuroaxial-cateter" label={t('formularios.f018a.bloqueF.regional.neuroaxial.cateter')} checked={valor.cateter} onChange={(v) => onChange({ cateter: v })} />
          <CampoTexto id="f018a-tec-neuroaxial-tipo-aguja" label={t('formularios.f018a.bloqueF.regional.neuroaxial.tipoAguja')} value={valor.tipoAgujaNeuroaxial} onChange={(v) => onChange({ tipoAgujaNeuroaxial: v })} />
          <CampoTexto id="f018a-tec-neuroaxial-numero-aguja" label={t('formularios.f018a.bloqueF.regional.neuroaxial.numeroAguja')} value={valor.numeroAgujaNeuroaxial} onChange={(v) => onChange({ numeroAgujaNeuroaxial: v })} />
          <CampoTexto id="f018a-tec-neuroaxial-intentos" label={t('formularios.f018a.bloqueF.regional.neuroaxial.intentos')} value={valor.intentosNeuroaxial} onChange={(v) => onChange({ intentosNeuroaxial: v })} />
          <SiNoBotones id="f018a-tec-neuroaxial-borbotaje" etiqueta={t('formularios.f018a.bloqueF.regional.neuroaxial.borbotaje')} valor={leerSiNo(valor.borbotaje)} textos={{ si: t('formulario.si'), no: t('formulario.no') }} onChange={(v) => onChange({ borbotaje: v })} />
          <fieldset aria-labelledby="f018a-tec-neuroaxial-acceso-titulo" className="m-0 flex min-w-0 flex-row flex-wrap items-center gap-4 border-0 p-0">
            <Label id="f018a-tec-neuroaxial-acceso-titulo">{t('formularios.f018a.bloqueF.regional.neuroaxial.acceso')}</Label>
            {ACCESO_NEUROAXIAL_OPCIONES.map((op) => (
              <CampoCheckbox
                key={op}
                id={`f018a-tec-neuroaxial-acceso-${op}`}
                label={t(`formularios.f018a.bloqueF.regional.neuroaxial.accesoOpciones.${op}`)}
                checked={valor.accesosNeuroaxial.includes(op)}
                onChange={(v) => onChange({ accesosNeuroaxial: v ? [...valor.accesosNeuroaxial, op] : valor.accesosNeuroaxial.filter((x) => x !== op) })}
              />
            ))}
          </fieldset>
          <CampoTexto id="f018a-tec-neuroaxial-sitio" label={t('formularios.f018a.bloqueF.regional.neuroaxial.sitioPuncion')} value={valor.sitioPuncion} onChange={(v) => onChange({ sitioPuncion: v })} />
          <CampoTexto id="f018a-tec-neuroaxial-dermatoma" label={t('formularios.f018a.bloqueF.regional.neuroaxial.dermatoma')} value={valor.dermatoma} onChange={(v) => onChange({ dermatoma: v })} />
          <CampoTexto id="f018a-tec-neuroaxial-posicion" label={t('formularios.f018a.bloqueF.regional.neuroaxial.posicion')} value={valor.posicionNeuroaxial} onChange={(v) => onChange({ posicionNeuroaxial: v })} />
        </div>
      )}
    </div>
  );
}

function BloqueSedoanalgesia({ valor, onChange }: { readonly valor: EstadoTecnicaSedoanalgesia; readonly onChange: (cambios: Partial<EstadoTecnicaSedoanalgesia>) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <Label>{t('formularios.f018a.bloqueF.sedoanalgesia.farmacos')}</Label>
        {valor.farmacos.map((f, i) => (
          <div key={f.id} className="flex items-end gap-2">
            <div className="flex-1">
              <CampoTexto
                id={`f018a-tec-sedo-farmaco-${f.id}`}
                label={`${t('formularios.f018a.bloqueF.sedoanalgesia.farmaco')} #${i + 1}`}
                value={f.texto}
                onChange={(v) => onChange({ farmacos: valor.farmacos.map((x) => (x.id === f.id ? { ...x, texto: v } : x)) })}
              />
            </div>
            <button type="button" className="pb-2" aria-label={t('formularios.f018a.bloqueF.sedoanalgesia.quitarFarmaco')} onClick={() => onChange({ farmacos: valor.farmacos.filter((x) => x.id !== f.id) })}>
              <Trash2 className="size-4 text-hce-muted" />
            </button>
          </div>
        ))}
        <Button type="button" variant="outline" className="w-fit" disabled={valor.farmacos.length >= TOPE_FARMACOS_SEDOANALGESIA} onClick={() => onChange({ farmacos: [...valor.farmacos, filaFarmacoSedoanalgesiaVacia()] })}>
          {t('formularios.f018a.bloqueF.sedoanalgesia.agregarFarmaco')}
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <CampoSelect id="f018a-tec-ramsay" label={t('formularios.f018a.bloqueF.sedoanalgesia.ramsay')} value={valor.ramsay} opciones={RAMSAY_NIVELES} etiquetaOpcion={(op) => op} onChange={(v) => onChange({ ramsay: v })} />
      </div>
    </div>
  );
}

export function TecnicaAnestesica({ estado, onChange }: { readonly estado: EstadoTecnicaAnestesica; readonly onChange: (estado: EstadoTecnicaAnestesica) => void }): JSX.Element {
  const { t } = useTranslation();

  function alMarcarCategoria(categoria: CategoriaTecnicaAnestesica, marcada: boolean): void {
    onChange({ ...estado, categorias: { ...estado.categorias, [categoria]: marcada } });
  }

  return (
    <div className="flex flex-col gap-4">
      <Label>{t('formularios.f018a.bloqueF.titulo')}</Label>
      <div className="flex flex-row flex-wrap gap-6">
        {CATEGORIAS_TECNICA.map((cat) => (
          <div key={cat} className="flex items-center gap-2">
            <Checkbox id={`f018a-tecnica-${cat}`} checked={estado.categorias[cat]} onCheckedChange={(v) => alMarcarCategoria(cat, v === true)} />
            <Label htmlFor={`f018a-tecnica-${cat}`} className="font-normal">
              {t(`formularios.f018a.bloqueF.categorias.${cat}`)}
            </Label>
          </div>
        ))}
      </div>

      {estado.categorias.general && <BloqueGeneral valor={estado.general} onChange={(cambios) => onChange({ ...estado, general: { ...estado.general, ...cambios } })} />}
      {estado.categorias.regional && <BloqueRegional valor={estado.regional} onChange={(cambios) => onChange({ ...estado, regional: { ...estado.regional, ...cambios } })} />}
      {estado.categorias.sedoanalgesia && <BloqueSedoanalgesia valor={estado.sedoanalgesia} onChange={(cambios) => onChange({ ...estado, sedoanalgesia: { ...estado.sedoanalgesia, ...cambios } })} />}
    </div>
  );
}
